import React from "react";
import { MenuItem, ModalSize } from "@shared/interfaces";
import {
  INVESTOR_PERMISSION,
  LEAD_PERMISSION,
  NICHE_FILES_PERMISSION,
  PROPERTIES_PERMISSION,
  SKIP_TRACING_PERMISSION,
  TICKET_PERMISSION,
  USER_PERMISSION,
} from "@shared/constants/permission";
import { Icon } from "@shared/components";
import { LINKS, NameOfChildRoutes, NameOfRoutes, MODAL_TYPES } from "@shared/constants";
import { Feature } from "@shared/models";
import { hasAccountActiveSubscription } from "@shared/utils";
import { ReactComponent as UserManagementIcon } from "@assets/files/icons/sidebar/redesign/user-management.svg";
import { ReactComponent as IntegrationsIcon } from "@assets/files/icons/sidebar/redesign/integrations.svg";
import { ReactComponent as LogoutIcon } from "@assets/files/icons/sidebar/redesign/logout.svg";
import { ReactComponent as SkipTracingIcon } from "@assets/files/icons/sidebar/redesign/skip-tracing.svg";
import { ReactComponent as StackedNicheFilesIcon } from "@assets/files/icons/sidebar/redesign/niche-files.svg";
import { ReactComponent as PayPerLeadIcon } from "@assets/files/icons/sidebar/redesign/pay-per-lead.svg";
import { ReactComponent as MarketMapIcon } from "@assets/files/icons/sidebar/redesign/market-map.svg";
import { ReactComponent as ResearchIcon } from "@assets/files/icons/sidebar/redesign/research.svg";
import { ReactComponent as CommunityIcon } from "@assets/files/icons/sidebar/redesign/community.svg";
import { ReactComponent as PredictiveAIIcon } from "@assets/files/icons/sidebar/redesign/predictive-ai.svg";
import { ReactComponent as ReisiftLogoIcon } from "@assets/files/icons/sidebar/redesign/reisift-logo.svg";
import { actions as sharedActions } from "@shared/store";

import config from "../../config";
import { store } from "../../index";

export const SIDEBAR_ITEMS: MenuItem[] = [
  {
    name: "Skip Tracing",
    path: `${NameOfChildRoutes.SKIP_TRACING.ROOT}`,
    icon: <SkipTracingIcon />,
    permission: SKIP_TRACING_PERMISSION.READ_SKIP_TRACING,
    hideForAdmin: true,
  },
  {
    name: "Stacked Niche Data",
    path: `${NameOfRoutes.NICHE_FILES}`,
    icon: <StackedNicheFilesIcon />,
    permission: NICHE_FILES_PERMISSION.READ_NICHE_FILES,
    feature: Feature.NicheFiles,
    hideForAdmin: true,
  },
  {
    name: "Company Management",
    path: `${NameOfChildRoutes.CONTACT.ROOT}`,
    icon: <Icon type="nav-users" />,
    permission: USER_PERMISSION.READ_ALL_USERS,
  },
  {
    name: "Data Delivery Updates",
    path: `${NameOfRoutes.DATA_DELIVERY_UPDATES}${NameOfChildRoutes.DATA_DELIVERY_UPDATES.DASHBOARD}`,
    icon: <Icon type="nav-data-delivery" />,
    permission: INVESTOR_PERMISSION.READ_HUBSPOT_PROPERTIES_HISTORY,
  },
  {
    name: "Predictive AI Data",
    path: `${NameOfChildRoutes.INVESTOR.ROOT}`,
    icon: <PredictiveAIIcon />,
    hideForAdmin: true,
    renderOnclick: (user) => {
      return !!user?.accounts.every((account) => !hasAccountActiveSubscription(account));
    },
    onClick: () => {
      window.open(LINKS.DATAFLIK_PROMO_DEMO, "_blank");
    },
  },
  {
    name: "Pay Per Lead",
    path: `${NameOfRoutes.PAY_PER_LEAD}${NameOfChildRoutes.PAY_PER_LEAD.DASHBOARD}`,
    icon: <PayPerLeadIcon />,
    feature: Feature.PayPerLead,
    permission: LEAD_PERMISSION.READ_LEADS,
    hideForAdmin: true,
  },
  {
    name: "Pay Per Lead",
    key: "pay_per_lead_admin",
    icon: <PayPerLeadIcon />,
    feature: Feature.PayPerLead,
    permission: LEAD_PERMISSION.READ_ALL_LEAD,
    childItems: [
      {
        name: "Leads",
        path: `/${NameOfRoutes.PAY_PER_LEAD}/${NameOfChildRoutes.PAY_PER_LEAD.LEADS}`,
      },
      {
        name: "Customers",
        path: `/${NameOfRoutes.PAY_PER_LEAD}/${NameOfChildRoutes.PAY_PER_LEAD.CUSTOMERS}`,
      },
      {
        name: "Subscriptions",
        path: `/${NameOfRoutes.PAY_PER_LEAD}/${NameOfChildRoutes.PAY_PER_LEAD.SUBSCRIPTIONS_LIST}`,
      },
      {
        name: "Disputes",
        permission: LEAD_PERMISSION.RESOLVE_LEAD_DISPUTE,
        path: `/${NameOfRoutes.PAY_PER_LEAD}/${NameOfChildRoutes.PAY_PER_LEAD.DISPUTES}`,
      },
      {
        name: "Rejected Leads",
        path: `/${NameOfRoutes.PAY_PER_LEAD}/${NameOfChildRoutes.PAY_PER_LEAD.REJECTED_LEADS}`,
      },
      {
        name: "Incomplete Leads",
        path: `/${NameOfRoutes.PAY_PER_LEAD}/${NameOfChildRoutes.PAY_PER_LEAD.INCOMPLETE_LEADS}`,
      },
    ],
  },
  {
    name: "Market Map",
    path: `${NameOfChildRoutes.MARKET_MAP.ROOT}`,
    icon: <MarketMapIcon />,
    feature: Feature.MarketMap,
  },
  {
    name: "Research",
    icon: <ResearchIcon />,
    feature: Feature.BackTestingAI,
    path: `${NameOfRoutes.RESEARCH}/transactions`,
    // childItems: [
    //   {
    //     name: "Sold Properties",
    //     path: `${NameOfRoutes.RESEARCH}/properties`,
    //   },
    // ],
  },
  {
    name: "Flik ID",
    path: `${NameOfRoutes.CLARITAS}${NameOfChildRoutes.CLARITAS.DASHBOARD}`,
    icon: <Icon type="nav-claritas" />,
    feature: Feature.FlikID,
  },

  {
    name: "Subscriptions",
    path: NameOfChildRoutes.SUBSCRIPTIONS.ROOT,
    icon: <Icon type="nav-subscriptions" />,
    hideForAdmin: true,
  },
  {
    name: "Property Search",
    path: `${NameOfRoutes.PROPERTIES}${NameOfChildRoutes.PROPERTIES.DASHBOARD}`,
    icon: <Icon type="nav-search-properties" />,
    permission: PROPERTIES_PERMISSION.READ_PROPERTIES,
  },
  {
    name: "County Management",
    key: "county_management",
    icon: <Icon type="nav-county-management" />,
    permission: LEAD_PERMISSION.READ_COUNTY_MANAGEMENT,
    childItems: [
      {
        name: "Counties",
        path: `/${NameOfRoutes.PAY_PER_LEAD}/${NameOfChildRoutes.PAY_PER_LEAD.COUNTIES}`,
      },
    ],
  },
  {
    name: "Tickets",
    path: NameOfRoutes.TICKETS,
    icon: <Icon type="nav-tickets" />,
    permission: TICKET_PERMISSION.READ_TICKETS,
    feature: Feature.Tickets,
  },
  {
    name: "Community",
    onClick: () => {
      window.open(config.communityUrl, "_blank");
    },
    icon: <CommunityIcon />,
    hideForAdmin: true,
    rightIcon: <Icon type="nav-external-link" />,
    className: "sidebar-item-footer",
  },
  {
    name: "REISift",
    onClick: () => {
      store.dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.REISIFT_INTEGRATION,
          size: ModalSize.RESIZABLE,
          props: {},
        }),
      );
    },
    icon: <ReisiftLogoIcon />,
    hideForAdmin: true,
    rightIcon: <Icon type="nav-external-link" />,
    feature: Feature.ReiSift,
  },
];

export const EXPANDED_USER_ITEMS = (onLogoutClicked: () => void): MenuItem[] => {
  return [
    {
      name: "User Management",
      path: NameOfRoutes.USER_MANAGEMENT,
      icon: <UserManagementIcon />,
      hideForAdmin: true,
      hidden: (user, account) => user?.id !== account?.user_id,
    },
    {
      name: "Integrations",
      path: NameOfRoutes.INTEGRATIONS,
      icon: <IntegrationsIcon />,
      hideForAdmin: true,
      hidden: (user, account) => user?.id !== account?.user_id,
    },
    {
      name: "Log Out",
      icon: <LogoutIcon />,
      onClick: onLogoutClicked,
    },
  ];
};
